import React, { useMemo } from 'react';

import TextNodes from '../components/TextNodes';
import { hasIconGlyph, IconGlyph } from '../components/icons/iconGlyph';
import {
  hasImageMargins,
  imgMaxSizeStyles,
  mergeMobileStyles,
  nativeIconGapStyles
} from '../styles';
import { adjustColor, getImageColorStyles } from '../../utils/styles';
import { isFit } from '../../utils/hydration';
import useBorder from '../components/useBorder';
import { hoverStylesGuard } from '../../utils/browser';
import ErrorInput from '../components/ErrorInput';
import { managedConnectAccountElement } from '../../integrations/connectAccount/providers';

// Space left between a scaled-down loader and the button's edge, per side. Not
// taken from the button's padding on purpose: that would tie the loader's size
// back to the content box, which is what collapsed it on an empty label.
const LOADER_BORDER_GAP = 2;

const BUTTON_IMAGE_SELECTOR = 'img, [data-feathery-button-icon]';

const getButtonImageFilterStyles = (color: string) => {
  if (!color) return {};
  return {
    [BUTTON_IMAGE_SELECTOR]: getImageColorStyles(color)
  };
};

function applyButtonStyles(element: any, responsiveStyles: any) {
  responsiveStyles.addTargets(
    'button',
    'buttonLabel',
    'buttonActive',
    'buttonHover',
    'buttonDisabled',
    'loader',
    'img'
  );

  responsiveStyles.applyBackgroundColorGradient('button');
  responsiveStyles.applyCorners('button');
  responsiveStyles.applyBoxShadow('button');
  responsiveStyles.applyFlexDirection('button');
  responsiveStyles.applyContentAlign('button');
  responsiveStyles.apply('button', 'entry_transition', (a: any) => {
    if (a === 'fade_in') {
      return {
        animation: 'fadeIn 1s',
        '@keyframes fadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 }
        }
      };
    }
    return {};
  });
  responsiveStyles.apply(
    'button',
    ['height', 'height_unit'],
    (a: any, b: any) => {
      if (b === 'px') return { minHeight: `${a}px` };
      return {};
    }
  );
  responsiveStyles.applyTextAlign('buttonLabel');
  responsiveStyles.apply(
    'button',
    [
      'uploader_padding_top',
      'uploader_padding_right',
      'uploader_padding_bottom',
      'uploader_padding_left'
    ],
    // @ts-expect-error TS(7006): Parameter 'a' implicitly has an 'any' type.
    (a, b, c, d) => ({
      padding: `${a}px ${b}px ${c}px ${d}px`
    })
  );
  responsiveStyles.applyWidth('img', 'image_');
  responsiveStyles.applyMargin('img', 'image_');
  responsiveStyles.applyColor('img', 'icon_color', 'color');
  responsiveStyles.apply('img', 'image_color', getImageColorStyles);
  responsiveStyles.apply('iconGap', 'flex_direction', nativeIconGapStyles);

  if (element.styles.hover_background_color) {
    responsiveStyles.applyColor(
      'buttonHover',
      `hover_background_color`,
      'background',
      true
    );
  } else {
    // default hover effect
    responsiveStyles.apply('buttonHover', 'background_color', (a: any) => {
      const newColor = `${adjustColor(a, -30)} !important`;
      return { background: newColor };
    });
  }
  responsiveStyles.applySpanSelectorStyles('buttonHover', 'hover_');
  responsiveStyles.apply(
    'buttonHover',
    'hover_image_color',
    getButtonImageFilterStyles
  );

  responsiveStyles.applyColor(
    'buttonActive',
    `selected_background_color`,
    'background',
    true
  );
  responsiveStyles.applySpanSelectorStyles('buttonActive', 'selected_');
  responsiveStyles.apply(
    'buttonActive',
    'selected_image_color',
    getButtonImageFilterStyles
  );

  responsiveStyles.apply('buttonDisabled', 'background_color', (a: any) => {
    const color = `${adjustColor(a, 30)} !important`;
    return {
      background: color,
      borderColor: color
    };
  });
  responsiveStyles.apply(
    'buttonDisabled',
    'disabled_image_color',
    getButtonImageFilterStyles
  );
  responsiveStyles.applySpanSelectorStyles('buttonDisabled', 'disabled_');
  responsiveStyles.applyColor(
    'buttonDisabled',
    `disabled_background_color`,
    'background',
    true
  );

  responsiveStyles.apply(
    'loader',
    ['height', 'height_unit'],
    (a: any, b: any) => {
      // Fit-height buttons have no numeric height, so size the loader
      // relative to the button text instead
      if (isFit(b)) return { width: '1em', height: '1em' };
      const dimension = `${Math.round(a / 2)}${b}`;
      // A percentage resolves against a different length on each axis, so it
      // would give a wide flat box and a spinner drawn tiny inside it. Take the
      // size from the height and let the ratio supply the width.
      return b === '%'
        ? { height: dimension, aspectRatio: '1' }
        : { width: dimension, height: dimension };
    }
  );

  return responsiveStyles;
}

function ButtonElement({
  element,
  responsiveStyles,
  loader = null,
  editMode,
  focused = false,
  disabled = false,
  hasClickLogic = false,
  active = null,
  textCallbacks = {},
  onClick = () => {},
  elementProps = {},
  inlineError,
  children,
  featheryContext
}: any) {
  const styles = useMemo(
    () => applyButtonStyles(element, responsiveStyles),
    [responsiveStyles]
  );
  const { borderStyles, customBorder } = useBorder({
    element,
    error: inlineError,
    defaultHover: true,
    breakpoint: responsiveStyles.getMobileBreakpoint()
  });

  const activeStyles = editMode
    ? styles.getTarget('button')
    : {
        ...styles.getTarget('buttonActive'),
        ...borderStyles.active
      };
  const hoverStyles = hoverStylesGuard(
    editMode
      ? styles.getTarget('button')
      : {
          ...styles.getTarget('buttonHover'),
          ...borderStyles.hover
        }
  );

  const actions = element.properties.actions ?? [];
  // A button with no click actions stays enabled if a logic rule runs on click
  const noActions =
    actions.length === 0 && !element.properties.submit && !hasClickLogic;

  // A Feathery-managed Connect Account label is computed, so it replaces the
  // builder's label on the canvas too - and can't be edited in place there.
  const managedElement = managedConnectAccountElement(element);
  const labelElement = managedElement ?? element;
  const labelEditMode =
    managedElement && editMode === 'editable' ? 'disabled' : editMode;

  const hasIcon = hasIconGlyph(element.properties.icon_glyph);
  const hasLabelContent = Boolean(
    String(labelElement.properties.text ?? '').trim()
  );
  let buttonImage = null;
  if (hasIcon) {
    const breakpoint = responsiveStyles.getMobileBreakpoint();
    const labelFontStyles = responsiveStyles.getRichFontStyles(
      element.properties.text_formatted?.[0]?.attributes ?? {}
    );
    const imgStyles = responsiveStyles.getTargets('img');
    const gapStyles =
      hasLabelContent && !hasImageMargins(element)
        ? responsiveStyles.getTarget('iconGap')
        : {};
    buttonImage = (
      <span
        data-feathery-button-icon={element.properties.icon_source}
        css={{
          display: 'inline-flex',
          alignItems: 'center',
          ...imgMaxSizeStyles,
          ...labelFontStyles,
          ...imgStyles,
          ...gapStyles,
          // A 1em icon scales with the label's mobile font size too.
          ...mergeMobileStyles(
            mergeMobileStyles(labelFontStyles, imgStyles, breakpoint),
            gapStyles,
            breakpoint
          )
        }}
      >
        <IconGlyph
          glyph={element.properties.icon_glyph}
          size={element.styles.image_width ? '100%' : '1em'}
        />
      </span>
    );
  } else if (element.properties.image) {
    buttonImage = (
      <img
        src={element.properties.image}
        css={{
          ...imgMaxSizeStyles,
          ...responsiveStyles.getTargets('img')
        }}
      />
    );
  }

  const buttonContent = (
    <>
      {buttonImage}
      {/* Keep labels mounted for icon-only and emptied editable buttons. */}
      {(labelElement.properties.text || labelEditMode) && (
        <TextNodes
          element={labelElement}
          responsiveStyles={responsiveStyles}
          cssTarget='buttonLabel'
          editMode={labelEditMode}
          disabled={disabled}
          focused={focused}
          textCallbacks={textCallbacks}
          featheryContext={featheryContext}
          expand={!element.properties.image && !hasIcon}
        />
      )}
    </>
  );
  const contentStyles = {
    display: 'contents',
    ...(loader ? ({ visibility: 'hidden' } as const) : {})
  } as const;
  // Draw the loader over the content instead of in place of it, centred in the
  // button's own box
  const loaderOverlayStyles = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  } as const;
  // The button never grows for its loader. A loader larger than the button
  // scales down to fit the box the button already has, keeping its ratio, and
  // stops short of the border so the corner radius has nothing to cut into.
  // A loader that already fits is untouched - the inset only bites on the
  // scaled-down ones.
  const loaderClampStyles = {
    maxWidth: `calc(100% - ${LOADER_BORDER_GAP * 2}px)`,
    maxHeight: `calc(100% - ${LOADER_BORDER_GAP * 2}px)`,
    '& img, & svg': {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain'
    }
  } as const;

  const buttonDisabled = !editMode && (noActions || loader || disabled);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (buttonDisabled) {
      e.preventDefault();
      return;
    }

    onClick?.(e);
  };

  return (
    <button
      id={element.id}
      key={element.id}
      type={element.properties.submit ? 'submit' : 'button'}
      className={active ? 'active' : undefined}
      style={{
        display: 'flex',
        cursor: editMode || buttonDisabled ? 'default' : 'pointer',
        width: '100%',
        height: '100%',
        position: 'relative',
        flex: 1,
        lineHeight: 'normal'
      }}
      css={{
        borderWidth: 0, // Prevent global CSS override if embedded
        justifyContent: 'center',
        alignItems: 'center',
        border: 'none',
        transition: '0.2s ease all !important',
        '&[aria-disabled="true"]': {
          cursor: 'default !important',
          ...styles.getTarget('buttonDisabled'),
          ...borderStyles.disabled
        },
        '&[aria-disabled="false"]:hover': hoverStyles,
        '&[aria-disabled="false"].active': activeStyles,
        // Fall back on default focus behavior if custom active state
        // is not set for button
        ...(active === null
          ? { '&[aria-disabled="false"]:focus': activeStyles }
          : {}),
        ...(element.properties.submit
          ? { '& > *': { pointerEvents: 'none' } }
          : {}),
        '&&&': styles.getTarget('button')
      }}
      // Use aria-disabled instead of disabled since disabled elements cannot display html errors
      aria-disabled={buttonDisabled}
      onClick={handleClick}
      aria-label={element.properties.aria_label}
      {...elementProps}
    >
      {customBorder}
      {children}
      {/* Always rendered so toggling the loader hides the content rather than
          unmounting it: a fit-sized button keeps its pre-loader dimensions,
          and the img isn't torn down and reloaded mid-click. `display:
          contents` generates no box, so the button's layout is unchanged. */}
      <span css={contentStyles}>{buttonContent}</span>
      {loader && (
        <div css={loaderOverlayStyles}>
          <div css={{ ...styles.getTarget('loader'), ...loaderClampStyles }}>
            {loader}
          </div>
        </div>
      )}
      {/* Hidden input so we can set field errors */}
      {!element.properties.submit && (
        <ErrorInput
          id={`error_${element.id}`}
          name={`error_${element.id}`}
          aria-label={element.properties.aria_label}
        />
      )}
    </button>
  );
}

export default ButtonElement;
